import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement} [emptyState] - The empty state element.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {boolean} */
  #isRenderingNextPage = false;

  /** @type {boolean} */
  #isRenderingPreviousPage = false;

  /** @type {number} */
  #renderGeneration = 0;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type, renderGeneration = this.#renderGeneration) {
    const page = this.#getPage(type);

    if (!this.#isCurrentRender(renderGeneration) || !page || !this.#shouldUsePage(page)) {
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url, renderGeneration);
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined, renderGeneration = this.#renderGeneration) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#isCurrentRender(renderGeneration) || !this.#shouldUsePage(pageInfo)) return;

    const sectionUrl = new URL(pageInfo.url.toString());
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, sectionUrl);
    if (!this.#isCurrentRender(renderGeneration)) return;

    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.#isRenderingNextPage) return;

    const renderGeneration = this.#renderGeneration;
    this.#isRenderingNextPage = true;

    try {
      await this.#renderNextPageContent(renderGeneration);
    } finally {
      if (this.#renderGeneration === renderGeneration) {
        this.#isRenderingNextPage = false;
        this.#syncEmptyStateVisibility();
      }
    }
  }

  async #renderNextPageContent(renderGeneration) {
    const { grid } = this.refs;

    if (!grid) return;

    let nextPage = this.#getPage('next');

    while (nextPage && this.#shouldUsePage(nextPage)) {
      const nextPageItemElements = await this.#getGridForFetchedPage(nextPage, renderGeneration);

      if (!this.#isCurrentRender(renderGeneration)) return;

      if (!nextPageItemElements) return;

      grid.dataset.currentPage = nextPage.page.toString();

      if (nextPageItemElements.length === 0) {
        nextPage = this.#getPage('next');
        continue;
      }

      grid.append(...nextPageItemElements);
      this.#setEmptyStateVisibility(false);

      this.#aspectRatioHelper?.processNewElements();

      history.pushState('', '', nextPage.url.toString());

      requestIdleCallback(() => {
        this.#fetchPage('next', renderGeneration);
      });

      return;
    }

  }

  async #renderPreviousPage() {
    if (this.#isRenderingPreviousPage) return;

    const renderGeneration = this.#renderGeneration;
    this.#isRenderingPreviousPage = true;

    try {
      await this.#renderPreviousPageContent(renderGeneration);
    } finally {
      if (this.#renderGeneration === renderGeneration) {
        this.#isRenderingPreviousPage = false;
        this.#syncEmptyStateVisibility();
      }
    }
  }

  async #renderPreviousPageContent(renderGeneration) {
    const { grid } = this.refs;

    if (!grid) return;

    let previousPage = this.#getPage('previous');
    while (previousPage && this.#shouldUsePage(previousPage)) {
      const previousPageItemElements = await this.#getGridForFetchedPage(previousPage, renderGeneration);

      if (!this.#isCurrentRender(renderGeneration)) return;

      if (!previousPageItemElements) return;

      grid.dataset.currentPage = previousPage.page.toString();

      if (previousPageItemElements.length === 0) {
        previousPage = this.#getPage('previous');
        continue;
      }

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);
      this.#setEmptyStateVisibility(false);

      this.#aspectRatioHelper?.processNewElements();

      history.pushState('', '', previousPage.url.toString());

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous', renderGeneration);
      });

      return;
    }

  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards, grid } = this.refs;
    const isPrevious = type === 'previous';

    const cardPages = Array.isArray(cards)
      ? cards.map((card) => Number(card.dataset.page)).filter(Boolean)
      : [];
    const currentGridPage = Number(grid?.dataset.currentPage);
    const loadedPages = currentGridPage ? [...cardPages, currentGridPage] : cardPages;

    if (loadedPages.length === 0) return;

    const currentCardPage = isPrevious ? Math.min(...loadedPages) : Math.max(...loadedPages);

    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {{ page: number, url?: URL }} pageInfo
   * @param {number} renderGeneration
   * @returns {Promise<NodeListOf<Element> | undefined>}
   */
  async #getGridForFetchedPage(pageInfo, renderGeneration) {
    let pageItemElements = this.#getGridForPage(pageInfo.page);

    if (!pageItemElements) {
      await this.#fetchSpecificPage(pageInfo.page, pageInfo.url, renderGeneration);
      if (!this.#isCurrentRender(renderGeneration)) return;

      pageItemElements = this.#getGridForPage(pageInfo.page);
    }

    return pageItemElements;
  }

  /**
   * @param {number} renderGeneration
   * @returns {boolean}
   */
  #isCurrentRender(renderGeneration) {
    return this.isConnected && renderGeneration === this.#renderGeneration;
  }

  /**
   * @returns {number}
   */
  #getRenderedCardCount() {
    return this.querySelectorAll('[ref="cards[]"]').length;
  }

  /**
   * @param {boolean} isVisible
   */
  #setEmptyStateVisibility(isVisible) {
    if (this.refs.emptyState) this.refs.emptyState.hidden = !isVisible;
  }

  #syncEmptyStateVisibility() {
    if (this.#isRenderingNextPage || this.#isRenderingPreviousPage) return;

    this.#setEmptyStateVisibility(this.#getRenderedCardCount() === 0);
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#renderGeneration += 1;
    const renderGeneration = this.#renderGeneration;
    this.#isRenderingNextPage = false;
    this.#isRenderingPreviousPage = false;
    this.pages.clear();

    let didHandleGridUpdate = false;
    const handleGridUpdate = () => {
      if (didHandleGridUpdate) return;

      didHandleGridUpdate = true;
      observer.disconnect();

      if (!this.#isCurrentRender(renderGeneration)) {
        return;
      }

      this.infinityScrollObserver?.disconnect();
      this.#observeViewMore();
      this.#syncEmptyStateVisibility();
      this.#fetchPage('previous', renderGeneration);
      this.#fetchPage('next', renderGeneration);
    };

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(handleGridUpdate);

    const { grid } = this.refs;
    const observedElement = grid ?? this;

    observer.observe(observedElement, {
      attributes: true,
      attributeFilter: ['data-last-page'],
      childList: true,
      subtree: !grid,
    });

    // Set a timeout as a fallback in case the mutation never fires
    setTimeout(() => {
      handleGridUpdate();
    }, 3000);
  };
}
